import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  Banner,
  Button,
  Icon,
  IndexTable,
  InlineStack,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { HashtagIcon, InfoIcon } from '@shopify/polaris-icons';
import { confirmable } from 'react-confirm';
import i18n from 'src/locales/i18n';
import { ProductLotCell } from 'src/components/cards/product_lot_cell';
import { requiredField } from 'src/utils/format_data';
import { fNumber } from 'src/utils/format_number';
import {
  formatExpirationInput,
  fStringToStringDate,
  isValidExpirationInput,
} from 'src/utils/format_time';
import StackedModal from './stacked_modal';

i18n.loadNamespaces(['stock_management', 'common', 'validation', 'helper_text']);

const ROWS_VISIBLE = 5;
const ROW_HEIGHT = 80;
const HEADER_HEIGHT = 40;

const QTY_CELL_MIN_WIDTH = 140;
const LOT_CELL_MIN_WIDTH = 140;
const EXP_CELL_MIN_WIDTH = 120;
const PRODUCT_CELL_WIDTH = '40%';
const isBlocked = (row) => (row.availableQuantity ?? 0) <= 0;

function LotExpirationBulkDialog({ show, proceed, title, items = [], getRowId }) {
  const rows = useMemo(
    () =>
      items
        .map((item, index) => ({
          ...item,
          rowId: getRowId ? getRowId(item) : `${index}`,
          currentLotNumber: item.lotNumber ?? '',
          currentExpirationDt: item.expirationDt
            ? fStringToStringDate(item.expirationDt) || item.expirationDt
            : '',
        }))
        .sort((a, b) => (isBlocked(a) ? 1 : 0) - (isBlocked(b) ? 1 : 0)),
    [items, getRowId],
  );

  const [inputs, setInputs] = useState({});
  const [bulkInput, setBulkInput] = useState({ quantity: '', lotNumber: '', expirationDt: '' });
  const [clamped, setClamped] = useState({});

  const inputOf = (row) => inputs[row.rowId] ?? {};

  const changeInput = (row, key, value) =>
    setInputs((prev) => ({ ...prev, [row.rowId]: { ...(prev[row.rowId] ?? {}), [key]: value } }));

  const valueOf = (row, key, fallback) => {
    const entered = inputOf(row)[key];
    return entered === undefined ? fallback : entered;
  };

  const lotOf = (row) => valueOf(row, 'lotNumber', row.currentLotNumber);
  const expOf = (row) => valueOf(row, 'expirationDt', row.currentExpirationDt);
  const quantityOf = (row) => valueOf(row, 'quantity', '');

  const expInvalid = (row) => {
    const value = expOf(row);
    if (!value) return false;
    return !isValidExpirationInput(value);
  };

  const expError = (row) => {
    const value = expOf(row);
    if (!value) return '';
    if (value.replace(/-/g, '').length < 8) return '';
    return expInvalid(row) ? i18n.t('validation:exp_format_invalid') : '';
  };

  const quantityError = (row) => {
    const quantity = parseInt(quantityOf(row), 10);
    if (!quantity) return true;
    return quantity > (row.availableQuantity ?? 0);
  };

  const blockedCount = rows.filter(isBlocked).length;
  const clampedCount = rows.filter((row) => clamped[row.rowId]).length;
  const bulkKeys = ['quantity', 'lotNumber', 'expirationDt'];
  const canApplyBulk = bulkKeys.some((key) => bulkInput[key] !== '');
  const clampQuantity = (row, digits) => {
    const max = row.availableQuantity ?? 0;
    const exceeded = digits !== '' && (parseInt(digits, 10) || 0) > max;
    return { value: exceeded ? `${max}` : digits, exceeded };
  };

  const setClampedFor = (rowIds, exceededIds) =>
    setClamped((prev) => {
      const next = { ...prev };
      rowIds.forEach((rowId) => {
        if (exceededIds.includes(rowId)) next[rowId] = true;
        else delete next[rowId];
      });
      return next;
    });

  const changeQuantity = (row, digits) => {
    const { value, exceeded } = clampQuantity(row, digits);
    changeInput(row, 'quantity', value);
    setClampedFor([row.rowId], exceeded ? [row.rowId] : []);
  };

  const applyAvailableToAll = () => {
    const targets = rows.filter((row) => !isBlocked(row));
    setInputs((prev) => {
      const next = { ...prev };
      targets.forEach((row) => {
        next[row.rowId] = { ...(next[row.rowId] ?? {}), quantity: `${row.availableQuantity ?? 0}` };
      });
      return next;
    });
    setClampedFor(
      targets.map((row) => row.rowId),
      [],
    );
  };

  const applyBulk = () => {
    const targets = rows.filter((row) => !isBlocked(row));
    const exceededIds = [];

    const quantities = {};
    if (bulkInput.quantity !== '') {
      targets.forEach((row) => {
        const { value, exceeded } = clampQuantity(row, bulkInput.quantity);
        quantities[row.rowId] = value;
        if (exceeded) exceededIds.push(row.rowId);
      });
    }

    setInputs((prev) => {
      const next = { ...prev };
      targets.forEach((row) => {
        const entry = { ...(next[row.rowId] ?? {}) };
        if (bulkInput.quantity !== '') entry.quantity = quantities[row.rowId];
        if (bulkInput.lotNumber !== '') entry.lotNumber = bulkInput.lotNumber;
        if (bulkInput.expirationDt !== '') entry.expirationDt = bulkInput.expirationDt;
        next[row.rowId] = entry;
      });
      return next;
    });

    if (bulkInput.quantity !== '')
      setClampedFor(
        targets.map((row) => row.rowId),
        exceededIds,
      );
  };
  const isChanged = (row) =>
    lotOf(row) !== row.currentLotNumber ||
    expOf(row) !== row.currentExpirationDt ||
    quantityOf(row) !== '';

  const changedRows = rows.filter((row) => !isBlocked(row) && isChanged(row));
  const canSave =
    changedRows.length > 0 && !changedRows.some((row) => quantityError(row) || expInvalid(row));

  const changeBulkInput = (key, value) => setBulkInput((prev) => ({ ...prev, [key]: value }));
  const handleBulkKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (canApplyBulk) applyBulk();
  };

  const bulkRowMarkup = (
    <IndexTable.Row
      id="apply-to-all"
      position={0}
      tone="subdued"
    >
      <Box
        component={IndexTable.Cell}
        sx={{ width: PRODUCT_CELL_WIDTH, verticalAlign: 'middle' }}
      >
        <InlineStack
          gap="100"
          blockAlign="center"
        >
          <Text
            as="span"
            variant="bodyMd"
            fontWeight="semibold"
          >
            {i18n.t('stock_management:apply_to_all')}
          </Text>
          <Tooltip content={i18n.t('helper_text:apply_to_all')}>
            <Box
              sx={{
                display: 'flex',
                width: 20,
                height: 20,
                '& .Polaris-Icon': { width: 20, height: 20, margin: 0 },
              }}
            >
              <Icon
                source={InfoIcon}
                tone="subdued"
              />
            </Box>
          </Tooltip>
        </InlineStack>
      </Box>
      <IndexTable.Cell />
      <IndexTable.Cell />
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: QTY_CELL_MIN_WIDTH, verticalAlign: 'middle' }}
      >
        <Box
          sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          onKeyDown={handleBulkKeyDown}
        >
          <TextField
            label={i18n.t('stock_management:field.change_quantity')}
            labelHidden
            align="right"
            value={bulkInput.quantity ? fNumber(bulkInput.quantity) : ''}
            autoComplete="off"
            onChange={(value) => changeBulkInput('quantity', value.replace(/\D/g, ''))}
            suffix={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& > span': { display: 'flex', alignItems: 'center' },
                }}
              >
                <Tooltip content={i18n.t('stock_management:set_change_qty')}>
                  <Button
                    variant="plain"
                    icon={HashtagIcon}
                    accessibilityLabel={i18n.t('stock_management:set_change_qty')}
                    onClick={applyAvailableToAll}
                  />
                </Tooltip>
              </Box>
            }
          />
        </Box>
      </Box>
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: LOT_CELL_MIN_WIDTH, verticalAlign: 'middle' }}
      >
        <Box
          sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          onKeyDown={handleBulkKeyDown}
        >
          <TextField
            label={i18n.t('stock_management:field.lot')}
            labelHidden
            value={bulkInput.lotNumber}
            autoComplete="off"
            onChange={(value) => changeBulkInput('lotNumber', value)}
          />
        </Box>
      </Box>
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: EXP_CELL_MIN_WIDTH, verticalAlign: 'middle' }}
      >
        <Box
          sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          onKeyDown={handleBulkKeyDown}
        >
          <TextField
            label={i18n.t('stock_management:field.exp')}
            labelHidden
            value={bulkInput.expirationDt}
            maxLength={10}
            inputMode="numeric"
            autoComplete="off"
            onChange={(value) => changeBulkInput('expirationDt', formatExpirationInput(value))}
          />
        </Box>
      </Box>
    </IndexTable.Row>
  );

  const rowMarkup = rows.map((row, index) => (
    <IndexTable.Row
      id={row.rowId}
      key={row.rowId}
      position={index + 1}
    >
      <Box
        component={IndexTable.Cell}
        sx={{ width: PRODUCT_CELL_WIDTH }}
      >
        <ProductLotCell
          product={{
            productNameLocal: row.productNameLocal,
            productNameEnglish: row.productNameEnglish,
            sku: row.sku,
            barcode: row.barcode,
            representativeImageUrl: row.representativeImageUrl,
            stockType: row.stockType,
            stockTypeNameLocal: row.stockTypeNameLocal,
            stockTypeNameEnglish: row.stockTypeNameEnglish,
          }}
          productWidth="80%"
        />
      </Box>
      <IndexTable.Cell>
        <Box sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'right' }}>
          <Text
            as="span"
            alignment="end"
            numeric
          >
            {fNumber(row.onHandQuantity ?? 0)}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'right' }}>
          <Text
            as="span"
            alignment="end"
            numeric
          >
            {fNumber(row.availableQuantity ?? 0)}
          </Text>
        </Box>
      </IndexTable.Cell>
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: QTY_CELL_MIN_WIDTH }}
      >
        <Box sx={{ width: '100%' }}>
          <TextField
            label={i18n.t('stock_management:field.change_quantity')}
            labelHidden
            placeholder={i18n.t('stock_management:field.change_quantity')}
            align="right"
            value={quantityOf(row) ? fNumber(quantityOf(row)) : ''}
            error={!isBlocked(row) && isChanged(row) && quantityError(row)}
            disabled={isBlocked(row)}
            autoComplete="off"
            onChange={(value) => changeQuantity(row, value.replace(/\D/g, ''))}
          />
        </Box>
      </Box>
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: LOT_CELL_MIN_WIDTH }}
      >
        <Box sx={{ width: '100%' }}>
          <TextField
            label={i18n.t('stock_management:field.lot')}
            labelHidden
            placeholder={i18n.t('stock_management:field.lot')}
            value={lotOf(row)}
            disabled={isBlocked(row)}
            autoComplete="off"
            onChange={(value) => changeInput(row, 'lotNumber', value)}
          />
        </Box>
      </Box>
      <Box
        component={IndexTable.Cell}
        sx={{ minWidth: EXP_CELL_MIN_WIDTH }}
      >
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            '& .Polaris-Labelled__Error': { position: 'absolute', top: '100%', left: 0, right: 0 },
          }}
        >
          <TextField
            label={i18n.t('stock_management:field.exp')}
            labelHidden
            placeholder="YYYY-MM-DD"
            value={expOf(row)}
            error={!isBlocked(row) && expError(row)}
            disabled={isBlocked(row)}
            maxLength={10}
            inputMode="numeric"
            autoComplete="off"
            onChange={(value) => changeInput(row, 'expirationDt', formatExpirationInput(value))}
          />
        </Box>
      </Box>
    </IndexTable.Row>
  ));

  return (
    <StackedModal
      isOpen={show}
      title={title || i18n.t('stock_management:edit_lot_and_exp')}
      sx={{ width: { xs: '95vw', sm: '95vw', md: '1080px' }, maxWidth: '95vw' }}
      content={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            whiteSpace: 'normal',
          }}
        >
          {blockedCount > 0 && (
            <Banner tone="warning">
              {i18n.t('stock_management:message.bulk_lot_exp_blocked', { count: blockedCount })}
            </Banner>
          )}
          {clampedCount > 0 && (
            <Banner tone="info">
              {i18n.t('stock_management:message.bulk_lot_exp_clamped', { count: clampedCount })}
            </Banner>
          )}
          <Box
            sx={{
              marginTop: blockedCount > 0 || clampedCount > 0 ? 'var(--p-space-200)' : 0,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              size="slim"
              disabled={!canApplyBulk}
              onClick={applyBulk}
            >
              {i18n.t('common:button.apply')}
            </Button>
          </Box>
          <Box
            sx={{
              marginTop: 'var(--p-space-200)',
              border: 'var(--p-border-width-025) solid var(--p-color-border)',
              borderRadius: 'var(--p-border-radius-300)',
              overflow: 'hidden',
              maxHeight: ROWS_VISIBLE * ROW_HEIGHT + HEADER_HEIGHT,
              overflowY: 'auto',
            }}
          >
            <IndexTable
              selectable={false}
              resourceName={{
                singular: i18n.t('common:field.product'),
                plural: i18n.t('common:field.product'),
              }}
              itemCount={rows.length}
              headings={[
                { title: i18n.t('stock_management:field.product') },
                { title: i18n.t('stock_management:field.on_hand'), alignment: 'end' },
                { title: i18n.t('stock_management:field.available'), alignment: 'end' },
                {
                  title: requiredField(i18n.t('stock_management:field.change_quantity')),
                  alignment: 'end',
                },
                { title: i18n.t('stock_management:field.lot') },
                { title: i18n.t('stock_management:field.exp') },
              ]}
            >
              {bulkRowMarkup}
              {rowMarkup}
            </IndexTable>
          </Box>
        </Box>
      }
      primaryAction={{
        content: i18n.t('common:button.save'),
        disabled: !canSave,
        onAction: () => {
          const changedItems = changedRows.map((row) => ({
            ...row,
            quantity: parseInt(quantityOf(row), 10) || 0,
            ...(lotOf(row) !== row.currentLotNumber ? { newLotNumber: lotOf(row) } : {}),
            ...(expOf(row) !== row.currentExpirationDt ? { newExpirationDt: expOf(row) } : {}),
          }));
          proceed({ confirmed: true, items: changedItems });
        },
      }}
      secondaryActions={[
        {
          content: i18n.t('common:button.cancel'),
          onAction: () => proceed({ confirmed: false, items: [] }),
        },
      ]}
      onClose={() => proceed({ confirmed: false, items: [] })}
    />
  );
}

export default confirmable(LotExpirationBulkDialog);
